/**
 * Theme + accent color infrastructure (Signa 2.0).
 *
 * Two preferences persisted in localStorage:
 *   - theme:  'system' | 'dark' | 'light'  -> sets [data-theme] on <html>
 *   - accent: one of ACCENTS keys          -> sets [data-accent] on <html>
 *
 * Accent colors differ between dark and light themes (darker hex in light mode
 * to preserve contrast), so we don't set --accent as an inline style - the CSS
 * rules in index.css pick the right value from the [data-theme][data-accent]
 * combination. `previewHex` below is only for UI swatches in the picker.
 */

export type ThemePref = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';
export type AccentName =
  | 'violet'
  | 'magenta'
  | 'electric'
  | 'cyan'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'indigo';

export const ACCENTS: Record<
  AccentName,
  { label: string; darkHex: string; lightHex: string; previewHex: string }
> = {
  violet: { label: 'Violet', darkHex: '#A78BFA', lightHex: '#7C3AED', previewHex: '#8B5CF6' },
  magenta: { label: 'Magenta', darkHex: '#E042A5', lightHex: '#C026D3', previewHex: '#E042A5' },
  electric: { label: 'Electric', darkHex: '#6B8CFF', lightHex: '#4C6EF5', previewHex: '#4C6EF5' },
  cyan: { label: 'Cyan', darkHex: '#22D3EE', lightHex: '#0891B2', previewHex: '#22D3EE' },
  emerald: { label: 'Emerald', darkHex: '#34D399', lightHex: '#059669', previewHex: '#10B981' },
  amber: { label: 'Amber', darkHex: '#FBBF24', lightHex: '#D97706', previewHex: '#F59E0B' },
  rose: { label: 'Rose', darkHex: '#FB7185', lightHex: '#E11D48', previewHex: '#F43F5E' },
  indigo: { label: 'Indigo', darkHex: '#818CF8', lightHex: '#4338CA', previewHex: '#6366F1' },
};

export const THEME_STORAGE_KEY = 'signa-theme';
export const ACCENT_STORAGE_KEY = 'signa-accent';

export const DEFAULT_THEME: ThemePref = 'system';
export const DEFAULT_ACCENT: AccentName = 'violet';

const THEME_VALUES: ThemePref[] = ['system', 'dark', 'light'];

export function isThemePref(v: unknown): v is ThemePref {
  return typeof v === 'string' && (THEME_VALUES as string[]).includes(v);
}

export function isAccentName(v: unknown): v is AccentName {
  return typeof v === 'string' && v in ACCENTS;
}

export function resolveTheme(pref: ThemePref): ResolvedTheme {
  if (pref === 'system') {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  }
  return pref;
}

export function applyTheme(pref: ThemePref): ResolvedTheme {
  const resolved = resolveTheme(pref);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved);
    document.documentElement.setAttribute('data-theme-pref', pref);
  }
  return resolved;
}

export function applyAccent(name: AccentName): void {
  if (!ACCENTS[name] || typeof document === 'undefined') return;
  // Clear any inline --accent override from older builds; CSS rules now own it.
  document.documentElement.style.removeProperty('--accent');
  document.documentElement.setAttribute('data-accent', name);
}

export function readStoredTheme(): ThemePref {
  if (typeof localStorage === 'undefined') return DEFAULT_THEME;
  const raw = localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePref(raw) ? raw : DEFAULT_THEME;
}

export function readStoredAccent(): AccentName {
  if (typeof localStorage === 'undefined') return DEFAULT_ACCENT;
  const raw = localStorage.getItem(ACCENT_STORAGE_KEY);
  return isAccentName(raw) ? raw : DEFAULT_ACCENT;
}

export function writeStoredTheme(pref: ThemePref): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(THEME_STORAGE_KEY, pref);
}

export function writeStoredAccent(name: AccentName): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(ACCENT_STORAGE_KEY, name);
}

export function initializeThemePreferences(): ResolvedTheme {
  const theme = readStoredTheme();
  const resolved = applyTheme(theme);
  applyAccent(readStoredAccent());
  return resolved;
}
